import type { Product } from 'eds-common';
import type { Entitlement } from '@/services/fulfillment/fetchEntitlements';
import type { Application } from '@/services/composite-service/composite-service';

export const ACCESS_CODE_QUERY_PARAM = 'accessCode';
export const REDEEM_QUERY_PARAM = 'redeem';
export const APP_QUERY_PARAM = 'app';
export const STATE_QUERY_PARAM = 'state';
export const ACCESS_CODE_STORAGE_KEY = 'accessCode';
export const REDEEM_STORAGE_KEY = 'redeem';
export const APP_STORAGE_KEY = 'app';
export const APP_STATE_STORAGE_KEY = 'appState';

const FALSY_FLAG_VALUES = new Set(['false', '0']);

const DEEP_LINK_QUERY_PARAMS = [
  ACCESS_CODE_QUERY_PARAM,
  REDEEM_QUERY_PARAM,
  APP_QUERY_PARAM,
  STATE_QUERY_PARAM,
];

export interface AccessCodeRedemptionInitialState {
  accessCode: string;
  product: Product;
}

export interface PendingAppLaunch {
  app: string;
  appState: string | null;
}

function normalizeDeepLinkValue(value?: string | null) {
  return value?.trim().toLowerCase() ?? null;
}

export function isEntitledToProductCode(
  entitlements: Entitlement[],
  productCode: string | null | undefined
) {
  const normalized = normalizeDeepLinkValue(productCode);
  if (!normalized) {
    return false;
  }
  return entitlements.some(
    (entitlement) =>
      normalizeDeepLinkValue(entitlement.productCode) === normalized
  );
}

export function hasApplicationAccess(
  applications: Application[],
  applicationCode: string | null | undefined
) {
  const normalized = normalizeDeepLinkValue(applicationCode);
  if (!normalized) {
    return false;
  }
  return applications.some(
    (application) =>
      normalizeDeepLinkValue(application.applicationCode) === normalized
  );
}

export function isSameDeepLinkCode(
  code: string | null | undefined,
  otherCode: string | null | undefined
) {
  const normalized = normalizeDeepLinkValue(code);
  return (
    Boolean(normalized) && normalized === normalizeDeepLinkValue(otherCode)
  );
}

export function clearStoredAppDeepLink() {
  const currentUrl = new URL(window.location.href);
  currentUrl.searchParams.delete(APP_QUERY_PARAM);
  currentUrl.searchParams.delete(STATE_QUERY_PARAM);
  const updatedUrl = `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`;

  window.history.replaceState(window.history.state, '', updatedUrl);

  sessionStorage.removeItem(APP_STORAGE_KEY);
  sessionStorage.removeItem(APP_STATE_STORAGE_KEY);
}

// Emptied here, not on dialog close: a reload or sign-out skips the close.
export function consumeStoredAppDeepLink(): PendingAppLaunch | null {
  const app = sessionStorage.getItem(APP_STORAGE_KEY);
  const appState = sessionStorage.getItem(APP_STATE_STORAGE_KEY);

  clearStoredAppDeepLink();

  return app ? { app, appState } : null;
}

export function isRedeemDeepLinkRequested(searchParams: URLSearchParams) {
  const value = searchParams.get(REDEEM_QUERY_PARAM);
  if (value === null) {
    return false;
  }
  return !FALSY_FLAG_VALUES.has(value.trim().toLowerCase());
}

export function clearAccessCodeDeepLink() {
  const currentUrl = new URL(window.location.href);

  DEEP_LINK_QUERY_PARAMS.forEach((param) =>
    currentUrl.searchParams.delete(param)
  );

  const updatedUrl = `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`;

  window.history.replaceState(window.history.state, '', updatedUrl);

  sessionStorage.removeItem(ACCESS_CODE_STORAGE_KEY);
  sessionStorage.removeItem(REDEEM_STORAGE_KEY);
}
